/**
 * /secretariat/dashboard — "Ma journée" style codevoiture.
 * Vue synthétique : leçons du jour + KPIs + accès rapides.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, GraduationCap, Clock, ArrowRight, Upload, FileText } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import api from '../../api';

const DAYS = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];
const MONTHS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

export default function SecretariatDashboardPage() {
  const { user } = useApp() || {};
  const [stats, setStats] = useState(null);
  const [lessonsToday, setLessonsToday] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Tableau de bord · Secrétariat';
    (async () => {
      try {
        const s = await api.boGetSecretariatStats();
        setStats(s);
      } catch (_) { /* silent */ }
      try {
        const today = new Date().toISOString().slice(0, 10);
        const lessons = await api.boListLessons({ dateFrom: today, dateTo: today });
        setLessonsToday(lessons?.items || []);
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const now = new Date();
  const dateStr = `${DAYS[now.getDay()]} ${now.getDate()} ${MONTHS[now.getMonth()]}`;
  const nbLessons = lessonsToday.length;

  return (
    <div className="max-w-5xl" data-testid="secretariat-dashboard">
      {/* Overline + titre */}
      <div className="mb-8">
        <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-amber-500 flex items-center gap-2 mb-2">
          Bonjour {user?.name || 'Administrateur'} <span className="text-white/30">·</span> Secrétariat
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Ma journée
        </h1>
        <p className="text-sm text-white/60 mt-2 max-w-2xl">
          Le déroulé du jour dans l’ordre : leçons et rendez-vous, puis la vue d’ensemble de l’auto-école.
        </p>
      </div>

      {/* Encart Aujourd'hui */}
      <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] to-amber-500/[0.02] p-6 sm:p-8 mb-6" data-testid="today-panel">
        <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-amber-400 mb-3">
          Aujourd&apos;hui <span className="text-white/30">·</span> {dateStr}
        </div>
        <div className="flex items-baseline gap-3">
          <div className="text-5xl sm:text-6xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="today-lessons-count">
            {nbLessons}
          </div>
          <div className="text-white/70 text-sm sm:text-base">
            {nbLessons > 1 ? 'leçons au planning' : 'leçon au planning'}
          </div>
        </div>
      </div>

      {/* Déroulé de la journée */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg sm:text-xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Déroulé de la journée
          </h2>
          <Link to="/secretariat/planning" data-testid="open-planning-btn" className="text-xs font-semibold text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">
            Planning complet <ArrowRight size={12} />
          </Link>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          {loading ? (
            <div className="text-white/40 text-sm text-center">Chargement…</div>
          ) : nbLessons === 0 ? (
            <div className="text-center py-4">
              <div className="text-white/60 text-sm mb-3">Aucune leçon planifiée aujourd&apos;hui.</div>
              <Link to="/secretariat/planning" className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm font-semibold">
                Ouvrir le planning <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-2" data-testid="today-lessons-list">
              {lessonsToday.slice(0, 5).map((l) => (
                <div key={l.id} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="w-1 h-8 rounded-full" style={{ background: l.teacher_color || '#f59e0b' }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-semibold truncate">{l.student_name || 'Sans élève'}</div>
                    <div className="text-white/50 text-xs">{l.teacher_name} · {l.duration_min} min</div>
                  </div>
                  <div className="text-white/70 text-sm font-mono">{(l.start_time || '').slice(11, 16)}</div>
                </div>
              ))}
              {nbLessons > 5 && (
                <div className="text-center text-white/40 text-xs pt-1">+ {nbLessons - 5} autres leçons</div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Vue d'ensemble */}
      <section className="mb-8">
        <h2 className="text-lg sm:text-xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Vue d&apos;ensemble
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="kpi-grid">
          <KpiCard icon={GraduationCap} label="Élèves actifs" value={stats?.eleves ?? '—'} color="#f59e0b" to="/secretariat/eleves" />
          <KpiCard icon={Users} label="Enseignants" value={stats?.enseignants ?? '—'} color="#0ea5e9" to="/secretariat/enseignants" />
          <KpiCard icon={Clock} label="Leçons aujourd’hui" value={stats?.lessons_today ?? nbLessons} color="#f43f5e" to="/secretariat/planning" />
        </div>
      </section>

      {/* Accès rapides */}
      <section>
        <h2 className="text-lg sm:text-xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Accès rapides
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <QuickAction icon={Calendar} label="Planning complet" to="/secretariat/planning" testid="quick-planning" />
          <QuickAction icon={GraduationCap} label="Fiches élèves" to="/secretariat/eleves" testid="quick-eleves" />
          <QuickAction icon={FileText} label="Formules & tarifs" to="/secretariat/formules" testid="quick-formules" />
          <QuickAction icon={Upload} label="Import & stats" to="/secretariat/stats" testid="quick-stats" />
          <QuickAction icon={Users} label="Enseignants" to="/secretariat/enseignants" testid="quick-enseignants" />
        </div>
      </section>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, to }) {
  return (
    <Link to={to} className="rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 p-5 transition group" data-testid="kpi-card">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${color}22`, color }}>
        <Icon size={16} />
      </div>
      <div className="text-3xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mt-1">{label}</div>
    </Link>
  );
}

function QuickAction({ icon: Icon, label, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="rounded-xl border border-white/10 bg-white/[0.02] hover:bg-amber-500/10 hover:border-amber-500/30 px-4 py-3.5 transition group flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center">
        <Icon size={15} />
      </div>
      <div className="text-sm text-white font-semibold flex-1">{label}</div>
      <ArrowRight size={14} className="text-white/30 group-hover:text-amber-400 group-hover:translate-x-0.5 transition" />
    </Link>
  );
}
